import { getProjectDashboardPageUrl } from "@Features/projects/utils/projectUrls";
import { getInspectionUrl, getViewerUrl } from "@Helpers";
import { getAllNotificationPageUrl } from "@Services/utils/helpers";
import { convertDate } from "../../services/luxonHelper";
import { getFormNotificationType, getProjectMailThreadsPageUrl } from "../../../../helpers/constants";
import { toObjectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import { Trans } from "@i18n";

/** A non-null object read as an open record: every member comes back as `unknown`. */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * One notification exactly as `getNotifications` projects it (notifications.service.ts: `id: "$_id"`
 * plus userId / createdDate / type / read / metadata), narrowed field by field — nothing asserted.
 *
 * THE SCHEMA GAP THIS USED TO DESCRIBE IS CLOSED. `notificationItemSchema` (server,
 * notifications.schema.ts) no longer declares `metadata` as flat scalars: it is
 * `z.record(z.string(), z.object({ value: … }))`, the `{ value }` wrapper the server actually
 * writes, and the feed page's `result` is `z.array(notificationItemSchema)` rather than
 * `z.unknown()`. So an importable type — `NotificationItem` — DOES describe this record now, and
 * `useAllNotification` hands this function exactly that.
 *
 * The defensive narrowing below nevertheless stays, and the reason is the OTHER caller:
 * `NotificationsPages.tsx` feeds rows in from SlickGrid1's `beforeDataBound`, declared
 * `(resp: RemoteDataResponse<T> | T[] | { result: T[] }) => T[]` (SlickGrid1/index.tsx:852). That
 * `T` is the grid's ROW type — `NotificationRow`, the post-transform display row — not the server's
 * `NotificationItem`, so the hook's own parameter type can never hand this function a typed feed
 * record; the grid widens it to `unknown` and narrows locally instead. Reaching `NotificationItem`
 * on that path would take an assertion, which this package does not allow, so retyping this
 * parameter would not remove a narrowing — it would move an `as` into the grid. `unknown` in plus
 * field-by-field narrowing stays the honest signature until `beforeDataBound` is generic over the
 * response body as well as the row.
 *
 * `metadata` stays `Record<string, unknown> | undefined` for the same reason, and because this
 * object is handed whole to mobile's `navigateForNotification`, whose `NotificationLike.metadata`
 * is `Record<string, unknown>` — `unknown` does not satisfy that. `readMetaText` reads one
 * `{ value }` entry at a time, which is the shape `notificationMetadataValue` now documents.
 */
interface NotificationRecord {
  /** Projected from the document `_id`, so present on every real record. The empty string is only
   *  the fallback for a response body that is not a notification at all. */
  id: ObjectIdString;
  userId: string | undefined;
  createdDate: number | undefined;
  read: string | undefined;
  type: string | undefined;
  metadata: Record<string, unknown> | undefined;
}

const toNotificationRecord = (value: unknown): NotificationRecord => {
  const raw: Record<string, unknown> = isRecord(value) ? value : {};
  return {
    id: toObjectIdString(typeof raw.id === "string" ? raw.id : ""),
    userId: typeof raw.userId === "string" ? raw.userId : undefined,
    createdDate: typeof raw.createdDate === "number" ? raw.createdDate : undefined,
    read: typeof raw.read === "string" ? raw.read : undefined,
    type: typeof raw.type === "string" ? raw.type : undefined,
    metadata: isRecord(raw.metadata) ? raw.metadata : undefined,
  };
};

/**
 * Reads `metadata.<key>.value` as the text a notification sentence interpolates.
 *
 * Every stored entry is `{ value }`, and every value is a string, a number, `null`, or an array of
 * file names (`NOTIFICATION_DATA_FILES_NAMES`); Mongo ObjectIds arrive JSON-serialised as strings.
 * So one text reader covers every `NOTIFICATION_DATA_*` key this renderer reads. An absent entry or
 * a `null` value reads as `undefined`, which i18next renders as an empty interpolation — the same
 * thing the `?.value` reads already produced.
 */
const readMetaText = (metadata: Record<string, unknown> | undefined, key: string): string | undefined => {
  const entry = metadata?.[key];
  if (!isRecord(entry)) return undefined;
  const value = entry.value;
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return undefined;
};

/**
 * `Bold` is the element the <Trans> `bold` slot renders — a tag name by default, `<b>`/`<strong>`
 * component from the callers.
 */
export const getNotificationParams = (rawNotification: unknown, Bold: React.ElementType = "strong") => {
  const notification = toNotificationRecord(rawNotification);
  /** `metadata.<key>.value` of this notification, as display text. */
  const meta = (key: string): string | undefined => readMetaText(notification.metadata, key);

  let text: React.JSX.Element | undefined;
  let redirectUrl: string | null | undefined;
  let type: string | undefined;
  const time = convertDate(Number(notification.createdDate), "getDifferenceTime");
  const id = notification.id;
  const userId = notification.userId;
  let username: string | undefined;
  let userName: string | undefined;
  let createdBy: string | undefined;
  let projectId: string | undefined;

  if (notification.type == "NOTIFICATION_TYPE_TRANSMITTAL_RECEIVED_TO") {
    const transmittalId = `TS-${meta("NOTIFICATION_DATA_TRANSMITTAL_COUNTER")}`;
    const spaceName = meta("NOTIFICATION_DATA_FROM_SPACE_NAME");

    text = (
      <>
        <Trans
          i18nKey="notification_transmittalReceived"
          values={{
            transmittalId,
            spaceName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    redirectUrl = `/project/${meta("NOTIFICATION_DATA_PROJECT_ID")}/listTransmittals/${meta("NOTIFICATION_DATA_TRANSMITTAL_ID")}?projectId=${meta("NOTIFICATION_DATA_PROJECT_ID")}`;
    type = "Transmittal";
  } else if (notification.type == "NOTIFICATION_TYPE_TRANSMITTAL_RECEIVED_CC") {
    const transmittalId = `TS-${meta("NOTIFICATION_DATA_TRANSMITTAL_COUNTER")}`;
    const spaceName = meta("NOTIFICATION_DATA_TO_SPACE_NAME");
    userName = meta("NOTIFICATION_DATA_FROM_USER_NAME") ?? "";
    text = (
      <>
        <Trans
          i18nKey="notification_transmittalSent"
          values={{
            transmittalId,
            spaceName,
            userName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    redirectUrl = null;
    type = "Transmittal";
  } else if (notification.type == "NOTIFICATION_TYPE_PROJECT_USER_ADDED") {
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    projectId = meta("NOTIFICATION_DATA_PROJECT_ID");
    text = (
      <>
        <Trans
          i18nKey="notification_addedToProject"
          values={{
            projectName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    redirectUrl = getProjectDashboardPageUrl(projectId);
    type = "Project";
  } else if (notification.type == "NOTIFICATION_TYPE_PROJECT_USER_REMOVED") {
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    projectId = meta("NOTIFICATION_DATA_PROJECT_ID");
    text = (
      <>
        <Trans
          i18nKey="notification_removedFromProject"
          values={{
            projectName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );

    type = "Project";
  } else if (notification.type == "NOTIFICATION_TYPE_PROJECT_USER_ROLE_CHANGED") {
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    const projectId = meta("NOTIFICATION_DATA_PROJECT_ID");
    const oldRoleName = meta("NOTIFICATION_DATA_FROM_ROLE_NAME");
    const newRoleName = meta("NOTIFICATION_DATA_TO_ROLE_NAME");
    text = (
      <>
        <Trans
          i18nKey="notification_roleChanges"
          values={{
            oldRoleName,
            newRoleName,
            projectName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    redirectUrl = getProjectDashboardPageUrl(projectId);
    type = "Setting";
  } else if (notification.type == "NOTIFICATION_TYPE_SPACE_USER_ADDED") {
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    const spaceName = meta("NOTIFICATION_DATA_SPACE_NAME");
    text = (
      <>
        <Trans
          i18nKey="notification_addedToSpace"
          values={{
            spaceName,
            projectName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );

    type = "Space";
  } else if (notification.type == "NOTIFICATION_TYPE_COMMENT_ADDED") {
    let comment = (meta("NOTIFICATION_DATA_COMMENT_CONTENT") ?? "").substring(0, 100);
    type = "Issue";
    comment = comment.replace(/<[^>]+>/g, "");
    username = meta("NOTIFICATION_DATA_COMMENT_ADDED_BY");
    const issueId = meta("NOTIFICATION_DATA_ISSUE_ID");
    const FileId = meta("NOTIFICATION_DATA_FILE_ID");
    const projectId = meta("NOTIFICATION_DATA_PROJECT_ID");
    const issueCounter = meta("NOTIFICATION_DATA_ISSUE_COUNTER");
    const spaceId = meta("NOTIFICATION_DATA_SPACE_ID");
    text = (
      <>
        <Trans
          i18nKey="notification_commentOn_Issue"
          values={{
            username,
            issueId: issueCounter,
            comment,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );

    const url = getViewerUrl({
      fileVersionId: FileId,
      issueId,
      spaceId,
      projectId,
    });
    redirectUrl = FileId && spaceId ? url : "";
  } else if (notification.type == "NOTIFICATION_TYPE_ISSUE_CREATED") {
    const ProjectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    username = meta("NOTIFICATION_DATA_ISSUE_CREATED_BY");
    const issueId = meta("NOTIFICATION_DATA_ISSUE_ID");
    const FileId = meta("NOTIFICATION_DATA_FILE_ID");
    const projectId = meta("NOTIFICATION_DATA_PROJECT_ID");
    const issueCounter = meta("NOTIFICATION_DATA_ISSUE_COUNTER");
    const spaceId = meta("NOTIFICATION_DATA_SPACE_ID");
    type = "Issue";
    text = (
      <>
        <Trans
          i18nKey="notification_assingedAnIssue"
          values={{
            username,
            issueId: issueCounter,
            ProjectName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );

    const url = getViewerUrl({
      fileVersionId: FileId,
      issueId,
      spaceId,
      projectId,
    });
    redirectUrl = FileId && spaceId ? url : "";
  } else if (notification.type == "NOTIFICATION_TYPE_FILE_CONVERSION_COMPLETED") {
    const fileName = meta("NOTIFICATION_DATA_FILE_NAME");
    const fileVersionId = meta("NOTIFICATION_DATA_FILE_VERSION_ID");
    const spaceId = meta("NOTIFICATION_DATA_SPACE_ID");
    const projectId = meta("NOTIFICATION_DATA_PROJECT_ID");
    type = "File";
    const url = getViewerUrl({
      fileVersionId,
      spaceId,
      projectId,
    });
    text = (
      <>
        <Trans
          i18nKey="notification_fileConversion"
          values={{
            fileName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );

    redirectUrl = url;
  } else if (notification.type == "NOTIFICATION_TYPE_TASK_USER_ADDED") {
    userName = meta("NOTIFICATION_DATA_FROM_USER_NAME") ?? "";
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    const spaceName = meta("NOTIFICATION_DATA_SPACE_NAME");
    const taskName = `TSK-${meta("NOTIFICATION_DATA_TASK_NAME")}`;
    text = (
      <>
        <Trans
          i18nKey="notification_taskAssigned"
          values={{
            userName,
            taskName,
            projectName,
            spaceName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );

    redirectUrl = `/project/${meta("NOTIFICATION_DATA_PROJECT_ID")}/listTask`;
    type = "Task";
  } else if (notification.type == "NOTIFICATION_TYPE_ESTIMATE_STATUS_UPDATED") {
    userName = meta("NOTIFICATION_DATA_STATUS_CHANGED_BY");
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    const spaceName = meta("NOTIFICATION_DATA_SPACE_NAME");
    const estimateName = meta("NOTIFICATION_DATA_ESTIMATE_NAME");
    const prevStatus = meta("NOTIFICATION_DATA_ESTIMATE_PREVIOUS_STATUS");
    const currentStatus = meta("NOTIFICATION_DATA_ESTIMATE_CURRENT_STATUS");
    type = "Estimate";
    text = (
      <>
        <Trans
          i18nKey="notification_estimateStatusChanges"
          values={{
            userName,
            estimateName,
            prevStatus,
            currentStatus,
            projectName,
            spaceName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_TASK_STATUS_CHANGED") {
    userName = meta("NOTIFICATION_DATA_FROM_USER_NAME") ?? "";
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    const spaceName = meta("NOTIFICATION_DATA_SPACE_NAME");
    const taskName = `TSK-${meta("NOTIFICATION_DATA_TASK_NAME")}`;
    let taskStatusName = meta("NOTIFICATION_DATA_TASK_STATUS_NAME") ?? "";
    taskStatusName =
      taskStatusName.charAt(0).toUpperCase() + taskStatusName.substring(1).toLocaleLowerCase().replace(/_/g, " ");
    text = (
      <>
        <Trans
          i18nKey="notification_taskStatusChanges"
          values={{
            userName,
            taskName,
            taskStatusName,
            projectName,
            spaceName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    redirectUrl = `/project/${meta("NOTIFICATION_DATA_PROJECT_ID")}/listTask/${meta("NOTIFICATION_DATA_TASK_VERSION_ID")}`;
    type = "Task";
  } else if (notification.type == "NOTIFICATION_TYPE_FILE_STATUS_CHANGED_UNDER_TRANSMITTAL") {
    // `type` is set once at the end of this branch, like every other branch here.
    userName = meta("NOTIFICATION_DATA_FROM_USER_NAME") ?? "";
    const fileName = meta("NOTIFICATION_DATA_FILE_NAME") || "";
    let fromStatus = meta("NOTIFICATION_DATA_FROM_STATUS") ?? "";
    let toStatus = meta("NOTIFICATION_DATA_TO_STATUS") ?? "";
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    const projectId = meta("NOTIFICATION_DATA_PROJECT_PROJECT_ID");
    const spaceName = meta("NOTIFICATION_DATA_SPACE_NAME");
    const note = meta("NOTIFICATION_DATA_NOTE") ?? "";

    fromStatus = fromStatus.charAt(0).toUpperCase() + fromStatus.substring(1).toLocaleLowerCase().replace(/_/g, " ");
    toStatus = toStatus.charAt(0).toUpperCase() + toStatus.substring(1).toLocaleLowerCase().replace(/_/g, " ");
    text = (
      <>
        <Trans
          i18nKey="notification_fileStatusChanges"
          values={{
            userName,
            fileName,
            fromStatus,
            toStatus,
            projectId,
            projectName,
            spaceName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );

    if (note) {
      text = (
        <>
          <Trans
            i18nKey="notification_textNote"
            values={{
              text,
              note,
            }}
            components={{ bold: <Bold /> }}
          />
        </>
      );
    }
    type = "File";
  } else if (notification.type == "NOTIFICATION_TYPE_REVIEW_PENDING") {
    type = "Review";
    redirectUrl = `/review?reviewId=${meta("NOTIFICATION_DATA_REVIEW_ID")}&spaceId=${meta("NOTIFICATION_DATA_SPACE_ID")}&projectId=${meta("NOTIFICATION_DATA_PROJECT_ID")}`;
    const reviewName = meta("NOTIFICATION_DATA_REVIEW_NAME");
    const reviewMsg = meta("NOTIFICATION_DATA_REVIEW_MESSAGE") || "";
    text = (
      <>
        <Trans
          i18nKey="notification_actionPedingForReview"
          values={{
            reviewMsg,
            reviewName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type === "NOTIFICATION_TYPE_REVIEW_FILE_VERSION_UPDATED") {
    type = "Review";
    redirectUrl = `/review?reviewId=${meta("NOTIFICATION_DATA_REVIEW_ID")}&spaceId=${meta("NOTIFICATION_DATA_SPACE_ID")}&projectId=${meta("NOTIFICATION_DATA_PROJECT_ID")}`;

    const fileNames = meta("NOTIFICATION_DATA_FILES_NAMES");
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    const notifierName = meta("NOTIFICATION_DATA_NOTIFIER_NAME");
    const reviewName = meta("NOTIFICATION_DATA_REVIEW_NAME");
    text = (
      <>
        <Trans
          i18nKey="notification_fileUpdatedToLatestVersion"
          values={{
            fileNames,
            projectName,
            notifierName,
            reviewName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_ROLLBACK_REVIEW_PENDING") {
    type = "Review";
    redirectUrl = `/review?reviewId=${meta("NOTIFICATION_DATA_REVIEW_ID")}&spaceId=${meta("NOTIFICATION_DATA_SPACE_ID")}&projectId=${meta("NOTIFICATION_DATA_PROJECT_ID")}`;
    const reviewName = meta("NOTIFICATION_DATA_REVIEW_NAME");
    const reviewMsg = meta("NOTIFICATION_DATA_REVIEW_MESSAGE") || "";
    text = (
      <>
        <Trans
          i18nKey="notification_actionPendingForRolledBackReview"
          values={{
            reviewMsg,
            reviewName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_REVIEW_CREATED") {
    type = "Review";
    redirectUrl = `/review?reviewId=${meta("NOTIFICATION_DATA_REVIEW_ID")}&spaceId=${meta("NOTIFICATION_DATA_SPACE_ID")}&projectId=${meta("NOTIFICATION_DATA_PROJECT_ID")}`;
    const reviewName = meta("NOTIFICATION_DATA_REVIEW_NAME");
    const reviewMsg = meta("NOTIFICATION_DATA_REVIEW_MESSAGE");
    text = (
      <>
        <Trans
          i18nKey="notification_reviewCreated"
          values={{
            reviewMsg,
            reviewName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_REVIEW_REVOKED") {
    type = "Review";
    redirectUrl = `/review?reviewId=${meta("NOTIFICATION_DATA_REVIEW_ID")}&spaceId=${meta("NOTIFICATION_DATA_SPACE_ID")}&projectId=${meta("NOTIFICATION_DATA_PROJECT_ID")}`;
    const reviewName = meta("NOTIFICATION_DATA_REVIEW_NAME");
    const reviewMsg = meta("NOTIFICATION_DATA_REVIEW_MESSAGE");
    text = (
      <>
        <Trans
          i18nKey="notification_reviewRevoked"
          values={{
            reviewMsg,
            reviewName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_REVIEW_DUE_DATE_APPROACHING") {
    type = "Review";
    redirectUrl = `/review?reviewId=${meta("NOTIFICATION_DATA_REVIEW_ID")}&spaceId=${meta("NOTIFICATION_DATA_SPACE_ID")}&projectId=${meta("NOTIFICATION_DATA_PROJECT_ID")}`;
    const reviewName = meta("NOTIFICATION_DATA_REVIEW_NAME");
    text = (
      <>
        <Trans
          i18nKey="notification_reviewDueDateApproaching"
          values={{
            reviewName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_REVIEW_OVERDUE") {
    type = "Review";
    redirectUrl = `/review?reviewId=${meta("NOTIFICATION_DATA_REVIEW_ID")}&spaceId=${meta("NOTIFICATION_DATA_SPACE_ID")}&projectId=${meta("NOTIFICATION_DATA_PROJECT_ID")}`;
    const reviewName = meta("NOTIFICATION_DATA_REVIEW_NAME");
    text = (
      <>
        <Trans
          i18nKey="notification_reviewOverdue"
          values={{
            reviewName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_MAIL_ACKNOWLEDGED") {
    type = "Mailbox";
    redirectUrl = getProjectMailThreadsPageUrl(
      meta("NOTIFICATION_DATA_PROJECT_ID") ?? "",
      meta("NOTIFICATION_DATA_MAIL_INSERTED_ID") ?? "",
    );
    const userName = meta("NOTIFICATION_DATA_MAIL_ACKNOWLEDGE_BY_NAME");
    const mailSubject = meta("NOTIFICATION_DATA_MAIL_SUBJECT");
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    text = (
      <>
        <Trans
          i18nKey="notification_mailAcknowledged"
          values={{
            userName,
            mailSubject,
            projectName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_RECEIVED_MAIL_TO") {
    type = "Mailbox";
    redirectUrl = getProjectMailThreadsPageUrl(
      meta("NOTIFICATION_DATA_PROJECT_ID") ?? "",
      meta("NOTIFICATION_DATA_MAIL_INSERTED_ID") ?? "",
    );
    const subject = meta("NOTIFICATION_DATA_MAIL_SUBJECT");
    const sendersName = meta("NOTIFICATION_DATA_MAIL_SENDERS_NAME");
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    text = (
      <>
        <Trans
          i18nKey="notification_mailRecieved"
          values={{
            sendersName,
            projectName,
            subject,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_RECEIVED_MAIL_CC") {
    type = "Mailbox";
    redirectUrl = getProjectMailThreadsPageUrl(
      meta("NOTIFICATION_DATA_PROJECT_ID") ?? "",
      meta("NOTIFICATION_DATA_MAIL_INSERTED_ID") ?? "",
    );
    const mailSubject = meta("NOTIFICATION_DATA_MAIL_SUBJECT");
    const sendersName = meta("NOTIFICATION_DATA_MAIL_SENDERS_NAME");
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    text = (
      <>
        <Trans
          i18nKey="notification_mailRecievedCC"
          values={{
            sendersName,
            projectName,
            mailSubject,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_MAIL_DUE_DATE_EXPIRED") {
    type = "Mailbox";
    redirectUrl = getProjectMailThreadsPageUrl(
      meta("NOTIFICATION_DATA_PROJECT_ID") ?? "",
      meta("NOTIFICATION_DATA_MAIL_INSERTED_ID") ?? "",
    );
    const responseType = meta("NOTIFICATION_DATA_RESPONSE_TYPE") == "REPLY_BY" ? "Reply" : "Acknowledge";
    const mailSub = meta("NOTIFICATION_DATA_MAIL_SUBJECT");
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    text = (
      <>
        <Trans
          i18nKey="notification_mailExpired"
          values={{
            responseType,
            mailSub,
            projectName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_DATA_FORM_CREATED") {
    createdBy = meta("NOTIFICATION_DATA_FORM_CREATEDBY");
    const title = meta("NOTIFICATION_DATA_FORM_TITLE");
    const projectId = meta("NOTIFICATION_DATA_FORM_PROJECTID");
    const resourceId = meta("NOTIFICATION_DATA_FORM_RESOURCEID");
    const contractId = meta("NOTIFICATION_DATA_FORM_CONTRACTID");
    const resourceType = meta("NOTIFICATION_DATA_FORM_RESOURCE_TYPE");
    const task = meta("NOTIFICATION_DATA_FORM_TASK");

    type = resourceType ? getFormNotificationType(resourceType) : undefined;
    if (task == "formCreated") {
      text = (
        <Trans
          i18nKey="notification_formCreated"
          values={{
            createdBy,
            title,
          }}
          components={{ bold: <Bold /> }}
        />
      );
    } else if (task == "levelSubmit") {
      text = (
        <Trans
          i18nKey="notification_formLevelSubmit"
          values={{
            title,
          }}
          components={{ bold: <Bold /> }}
        />
      );
    } else if (task == "finalSubmit") {
      text = (
        <Trans
          i18nKey="notification_formSubmit"
          values={{
            title,
          }}
          components={{ bold: <Bold /> }}
        />
      );
    } else if (task == "rollback") {
      text = (
        <Trans
          i18nKey="notification_formRollBackSubmit"
          values={{
            title,
          }}
          components={{ bold: <Bold /> }}
        />
      );
    } else if (task == "formRejected") {
      text = (
        <Trans
          i18nKey="notification_formRejected"
          values={{
            title,
          }}
          components={{ bold: <Bold /> }}
        />
      );
    } else if (task == "formApproved") {
      text = (
        <Trans
          i18nKey="notification_formApproved"
          values={{
            title,
          }}
          components={{ bold: <Bold /> }}
        />
      );
    }

    redirectUrl = resourceId
      ? getInspectionUrl({
          projectId,
          resourceId,
          contractId,
          resourceType,
        })
      : null;
  } else if (
    notification.type == "NOTIFICATION_TYPE_CHAT_MESSAGE" ||
    notification.type == "NOTIFICATION_TYPE_CHAT_MESSAGE_GROUP" ||
    notification.type == "NOTIFICATION_TYPE_CHAT_MESSAGE_SPACE" ||
    notification.type == "NOTIFICATION_TYPE_CHAT_MENTION"
  ) {
    type = "Chat";
    const senderName = meta("NOTIFICATION_DATA_CHAT_SENDER_NAME") ?? "";
    const conversationName = meta("NOTIFICATION_DATA_CHAT_CONVERSATION_NAME") ?? "";
    const spaceName = meta("NOTIFICATION_DATA_SPACE_NAME") ?? "";
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME") ?? "";
    const conversationType = meta("NOTIFICATION_DATA_CHAT_CONVERSATION_TYPE") ?? "";
    let chatKey: string;
    if (notification.type == "NOTIFICATION_TYPE_CHAT_MESSAGE") chatKey = "notification_chatMessage";
    else if (notification.type == "NOTIFICATION_TYPE_CHAT_MESSAGE_GROUP") chatKey = "notification_chatMessageGroup";
    else if (notification.type == "NOTIFICATION_TYPE_CHAT_MESSAGE_SPACE") chatKey = "notification_chatMessageSpace";
    else chatKey = conversationType === "direct" ? "notification_chatMention" : "notification_chatMentionIn";
    text = (
      <>
        <Trans
          i18nKey={chatKey}
          values={{
            senderName,
            conversationName: conversationType === "space" ? spaceName : conversationName,
            spaceName,
            projectName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    // The chat panel is not a route; the header icon opens it, so the row itself does not navigate.
    redirectUrl = null;
  } else if (notification.type == "NOTIFICATION_DATA_FILE_UPLOADED") {
    type = "File";
    const files = meta("NOTIFICATION_DATA_FILE_ESCROW_BATCH_FILES");
    const batchId = meta("NOTIFICATION_DATA_FILE_ESCROW_BATCH_ID");
    text = (
      <>
        <Trans
          i18nKey="notification_fileUploaded"
          values={{
            files,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    redirectUrl = `/escrowFiles/${batchId}`;
  } else if (notification.type == "NOTIFICATION_DATA_FORM_DUEDATE_NOTIFICATION") {
    type = "GeneralForms";
    createdBy = meta("NOTIFICATION_DATA_FORM_PROJECTID");
    const formTitle = meta("NOTIFICATION_DATA_FORM_TITLE");
    const formNo = meta("NOTIFICATION_DATA_FORM_NO");
    const formDueDate = meta("NOTIFICATION_DATA_FORM_DUEDATE");
    const projectId = meta("NOTIFICATION_DATA_FORM_PROJECTID");
    const resourceId = meta("NOTIFICATION_DATA_FORM_RESOURCEID");
    const contractId = meta("NOTIFICATION_DATA_FORM_CONTRACTID");
    redirectUrl = getInspectionUrl({ projectId, resourceId, contractId });
    const formConvertedDueDate = convertDate(Number(formDueDate), "dateForToolTipModal");
    text = (
      <>
        <Trans
          i18nKey="notification_formExpired"
          values={{
            formNo,
            formTitle,
            formConvertedDueDate,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
  } else if (notification.type == "NOTIFICATION_TYPE_SUPPORT_TICKET_CREATED") {
    type = "Support";
    const ticketKey = meta("NOTIFICATION_DATA_SUPPORT_TICKET_KEY");
    const ticketId = meta("NOTIFICATION_DATA_SUPPORT_TICKET_ID");
    text = (
      <>
        <Trans
          i18nKey="notification_supportTicketCreated"
          values={{
            ticketKey,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    // `ticketId` comes off the notification metadata bag, so it is `string | undefined`, while
    // `getAllNotificationPageUrl` declares its params optional-and-absent. It only sets the query
    // param behind `if (ticketId)`, so an absent key and a present-but-undefined key build the same
    // URL — the same spread is used at the three sibling support branches below.
    redirectUrl = getAllNotificationPageUrl({ section: "support", ...(ticketId !== undefined && { ticketId }) });
  } else if (notification.type == "NOTIFICATION_TYPE_SUPPORT_COMMENT_ADDED") {
    type = "Support";
    const ticketKey = meta("NOTIFICATION_DATA_SUPPORT_TICKET_KEY");
    const ticketId = meta("NOTIFICATION_DATA_SUPPORT_TICKET_ID");
    const actorName = meta("NOTIFICATION_DATA_SUPPORT_ACTOR_NAME");
    const comment = meta("NOTIFICATION_DATA_SUPPORT_COMMENT");
    text = (
      <>
        <Trans
          i18nKey="notification_supportCommentAdded"
          values={{
            actorName,
            ticketKey,
            comment,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    redirectUrl = getAllNotificationPageUrl({ section: "support", ...(ticketId !== undefined && { ticketId }) });
  } else if (notification.type == "NOTIFICATION_TYPE_SUPPORT_STATUS_CHANGED") {
    type = "Support";
    const ticketKey = meta("NOTIFICATION_DATA_SUPPORT_TICKET_KEY");
    const ticketId = meta("NOTIFICATION_DATA_SUPPORT_TICKET_ID");
    const fromStatus = meta("NOTIFICATION_DATA_SUPPORT_FROM_STATUS");
    const toStatus = meta("NOTIFICATION_DATA_SUPPORT_TO_STATUS");
    text = (
      <>
        <Trans
          i18nKey="notification_supportStatusChanged"
          values={{
            ticketKey,
            fromStatus,
            toStatus,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    redirectUrl = getAllNotificationPageUrl({ section: "support", ...(ticketId !== undefined && { ticketId }) });
  } else if (notification.type == "NOTIFICATION_TYPE_SUPPORT_TYPE_CHANGED") {
    type = "Support";
    const ticketKey = meta("NOTIFICATION_DATA_SUPPORT_TICKET_KEY");
    const ticketId = meta("NOTIFICATION_DATA_SUPPORT_TICKET_ID");
    const fromType = meta("NOTIFICATION_DATA_SUPPORT_FROM_TYPE");
    const toType = meta("NOTIFICATION_DATA_SUPPORT_TO_TYPE");
    const fromStatus = meta("NOTIFICATION_DATA_SUPPORT_FROM_STATUS");
    const toStatus = meta("NOTIFICATION_DATA_SUPPORT_TO_STATUS");
    const toImpact = meta("NOTIFICATION_DATA_SUPPORT_TO_IMPACT");
    text = (
      <>
        <Trans
          i18nKey={
            fromStatus && toStatus ? "notification_supportTypeAndStatusChanged" : "notification_supportTypeChanged"
          }
          values={{
            ticketKey,
            fromType,
            toType,
            fromStatus,
            toStatus,
          }}
          components={{ bold: <Bold /> }}
        />
        {toImpact && (
          <Trans i18nKey="notification_supportImpactSuffix" values={{ toImpact }} components={{ bold: <Bold /> }} />
        )}
      </>
    );
    redirectUrl = getAllNotificationPageUrl({ section: "support", ...(ticketId !== undefined && { ticketId }) });
  } else if (notification.type == "NOTIFICATION_DATA_USER_ADDED_IN_FORM") {
    const sender = meta("NOTIFICATION_DATA_FORM_CREATEDBY");
    const formTitle = meta("NOTIFICATION_DATA_FORM_TITLE");
    const formCategoryName = meta("NOTIFICATION_DATA_FORM_RESOURCE_TYPE_NAME");
    const projectName = meta("NOTIFICATION_DATA_PROJECT_NAME");
    const projectId = meta("NOTIFICATION_DATA_FORM_PROJECTID");
    const contractId = meta("NOTIFICATION_DATA_FORM_FORM_CONTRACTID");
    const resourceId = meta("NOTIFICATION_DATA_FORM_FORMID");
    const resourceType = meta("NOTIFICATION_DATA_FORM_RESOURCE_TYPE");
    type = resourceType ? getFormNotificationType(resourceType) : undefined;
    text = (
      <>
        <Trans
          i18nKey={"notification_user_added_in_form"}
          values={{
            sender,
            formTitle,
            formCategoryName,
            projectName,
          }}
          components={{ bold: <Bold /> }}
        />
      </>
    );
    redirectUrl = getInspectionUrl({
      projectId,
      resourceId,
      contractId,
      resourceType,
    });
  }

  return {
    text,
    time,
    id,
    redirectUrl,
    createdDate: notification.createdDate,
    type,
    read: notification.read,
    userId,
    username,
    userName,
    createdBy,
    projectId,
    metadata: notification.metadata,
  };
};
